import type { GroupStyleI, NumberInputT, SimpleStyleT } from "tweenly"
import TextField from "@mui/material/TextField"
import { ChangeEvent, KeyboardEvent, useRef } from "react"
import { getStyleFieldLabel } from "../../../helpers/properties.helpers"
import { useController } from "../../../hooks/useController"
import useKeyDown from "../../../hooks/useKeyDown"
import { endStepRun, markStep } from "../../../helpers/stepUndoGroup.helpers"
import { isStepChange, stepNumber, useWheelStep } from "../../../hooks/useNumberStep"

interface Props {
    styleProperty: SimpleStyleT | GroupStyleI
    handleOnChange: (e: any) => void
}

/**
 * Commits on blur and Enter — and at once when a number is stepped (spin arrows, ↑ / ↓,
 * the mouse wheel), see useNumberStep.
 */
const NumberInput = ({ styleProperty, handleOnChange }: Props) => {
    const { value, onChange } = useController(styleProperty.value)
    const { handleOnEnterKeyDown } = useKeyDown()
    const inputRef = useRef<HTMLInputElement>(null)

    const isNumber = styleProperty.input.type === "number"
    const limits = isNumber ? (styleProperty.input as NumberInputT).props?.inputProps : undefined

    const label: string = getStyleFieldLabel(styleProperty)

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e)
        if (isNumber && isStepChange(e)) {
            markStep(e.target)
            handleOnChange(e)
        }
    }

    useWheelStep(
        inputRef,
        (direction) => {
            const next = String(
                stepNumber(Number(value), direction, limits?.step, limits?.min, limits?.max)
            )
            onChange({ target: { value: next } } as ChangeEvent<HTMLInputElement>)
            markStep(inputRef.current)
            handleOnChange({ target: { name: styleProperty.property, value: next } })
        },
        isNumber
    )

    /**
     * Typed below the style's minimum (a negative width): nothing is written and the field
     * goes back to the last valid value. Stepping already stops at the minimum.
     */
    const commit = (e: any) => {
        // a typed value is an undo step of its own, not part of a stepping run
        endStepRun()
        const typed = String(e?.target?.value ?? "")
        const min = limits?.min
        if (min !== undefined && typed.trim() !== "" && Number(typed) < min) {
            onChange({ target: { value: styleProperty.value } } as ChangeEvent<HTMLInputElement>)
            return
        }
        handleOnChange(e)
    }

    return (
        <TextField
            type={styleProperty.input.type}
            label={label}
            name={styleProperty.property}
            value={value}
            onChange={handleChange}
            inputRef={inputRef}
            onBlur={commit}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleOnEnterKeyDown(e, commit)}
            InputProps={(styleProperty.input as NumberInputT).props}
            InputLabelProps={{
                shrink: true,
            }}
        />
    )
}

export default NumberInput
